
import React, { useState, useEffect } from 'react';
import './AttachmentsModal.scss'; // We'll create this SCSS file next
import { config } from '../../utils/config';

interface Attachment {
    id: string;
    name: string;
    size: number;
    content_type: string;
    date: string;
    subject?: string;
    download_url?: string;  // URL from main RFQ backend
}

interface AttachmentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    rfqId: string;
    supplierId: string;
    supplierName: string;
    // Add token prop or use context hook inside. 
    // Assuming context hook usage based on other files.
}

const AttachmentsModal: React.FC<AttachmentsModalProps> = ({
    isOpen,
    onClose,
    rfqId,
    supplierId,
    supplierName
}) => {
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // We need to import invoiceService to fetch data
    // Or fetch directly if service update is pending.
    // Let's assume we'll update service first or fetch here.
    // Fetching here for simplicity then refactor to service.

    useEffect(() => {
        if (isOpen && rfqId && supplierId) {
            fetchAttachments();
        }
    }, [isOpen, rfqId, supplierId]);

    const fetchAttachments = async () => {
        setLoading(true);
        setError(null);
        try {
            // Helper to get token - reusing logic seen in other components
            // In a real app, useAuth hook.
            const token = localStorage.getItem('auth_token');
            const url = `${config.API_URL}/api/invoices/completed-rfqs/${rfqId}/${supplierId}/attachments`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (result.success) {
                setAttachments(result.data);
            } else {
                setError(result.message || 'Failed to load attachments');
            }
        } catch (err) {
            console.error(err);
            setError('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (attachment: Attachment) => {
        try {
            const token = localStorage.getItem('auth_token');

            // Use invoice-checker backend proxy to avoid CORS with main backend
            // URL format: /api/invoices/attachments/{rfq_id}/{attachment_id}/download
            const proxyUrl = `${config.API_URL}/api/invoices/attachments/${rfqId}/${attachment.id}/download`;

            const response = await fetch(proxyUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = attachment.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);

        } catch (err) {
            console.error('Download error:', err);
            alert('Failed to download file');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content attachments-modal">
                <div className="modal-header">
                    <h3>Attachments - {supplierName}</h3>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="loading">Loading attachments...</div>
                    ) : error ? (
                        <div className="error">{error}</div>
                    ) : attachments.length === 0 ? (
                        <div className="empty-state">No attachments found.</div>
                    ) : (
                        <ul className="attachments-list">
                            {attachments.map(att => (
                                <li key={att.id} className="attachment-item">
                                    <div className="file-info">
                                        <div className="file-name">{att.name}</div>
                                        <div className="file-meta">
                                            {(att.size / 1024).toFixed(1)} KB • {new Date(att.date).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <button
                                        className="download-btn"
                                        onClick={() => handleDownload(att)}
                                    >
                                        Download
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttachmentsModal;
